import React, { useCallback, useMemo, useRef } from 'react';
import { devWarning, OmitStrict } from '@tanium/coreui-utils';
import styled from '@tanium/react-emotion';
import { useAggregatedRef } from '@tanium/react-use-aggregated-ref';
import { css as taniumCss } from '@tanium/style-system';
import { UnitInputContainer, UnitLabel } from './StyledUnitLabelComponents';
import {
  Border,
  BorderDisabled,
  BorderFocus,
  BorderInvalid,
  Fill,
  FillDisabled,
  PlaceholderText,
  ShadowFocus,
  Text,
  TextDisabled,
} from './themeDefinitions';

const StyledTextInput = styled('input', {
  shouldForwardProp: (p) => !['invalid', 'componentSize', 'width'].includes(p),
})<Pick<Props, 'invalid' | 'componentSize' | 'width'>>(({ invalid, componentSize, width }) =>
  taniumCss({
    bg: Fill,
    border: 1,
    borderColor: invalid ? BorderInvalid : Border,
    borderRadius: 1,
    color: Text,
    height: componentSize === 'sm' ? 3 : 4,
    lineHeight: '16px',
    width,
    p: 1,
    fontSize: componentSize === 'sm' ? 12 : 14,
    '::placeholder': {
      color: PlaceholderText,
    },
    ':disabled': {
      bg: FillDisabled,
      borderColor: BorderDisabled,
      color: TextDisabled,
      // explicitly override input disabled color for Safari
      // see: https://stackoverflow.com/a/4648315
      ['WebkitTextFillColor' as string]: TextDisabled,
    },
    ':focus': {
      borderColor: BorderFocus,
      boxShadow: ShadowFocus,
      // use transparent color instead of `outline: none` for Windows high contrast
      // see: https://stackoverflow.com/a/52616313
      outlineColor: 'transparent',
      // not setting outline style causes outline to appear white in Chrome
      outlineStyle: 'inherit',
    },
  }),
);

export const textInputTypes = [
  'date',
  'datetime-local',
  'email',
  'month',
  'number',
  'password',
  'search',
  'tel',
  'text',
  'time',
  'url',
  'week',
] as const;

export type TextInputType = (typeof textInputTypes)[number];
type TextInputSize = 'sm' | 'lg';

const limitedSupportTypes: TextInputType[] = ['date', 'datetime-local', 'month', 'time', 'week'];

const unitInputCompatibleTypes: string[] = ['text', 'number', 'url'];

export interface UnitInputProps extends Props {
  /**
   * Display label at beginning or end
   */
  unitLabelType: 'prefix' | 'suffix';
  /**
   * Unit of measure the input represents.
   */
  unitLabel: string;
}

const UnitInput = React.forwardRef<HTMLInputElement, UnitInputProps>((props, refProp) => {
  const { unitLabel, unitLabelType, disabled, invalid, width, ...filteredProps } = props;
  const focusRef = useRef<HTMLInputElement>(null);
  const ref = useAggregatedRef(useMemo(() => [focusRef, refProp], [refProp]));

  const handleClickCapture = useCallback(() => {
    // Bring focus to input whenever the outer container is clicked
    focusRef.current?.focus();
  }, []);

  return (
    <UnitInputContainer
      disabled={disabled}
      width={width}
      invalid={invalid}
      onClickCapture={handleClickCapture}
    >
      {unitLabel && unitLabelType === 'prefix' && <UnitLabel>{unitLabel}</UnitLabel>}
      {/* eslint-disable-next-line @typescript-eslint/no-use-before-define -- mutually recursive; terminating case is lack of `unitLabel` */}
      <UnitInputTextInput ref={ref} disabled={disabled} {...filteredProps} />
      {unitLabel && unitLabelType === 'suffix' && <UnitLabel>{unitLabel}</UnitLabel>}
    </UnitInputContainer>
  );
});
export interface Props extends OmitStrict<JSX.IntrinsicElements['input'], 'type' | 'ref'> {
  type?: TextInputType;
  /**
   * Whether the input should be displayed in an invalid (error) state.
   */
  invalid?: boolean;
  /**
   * Allows a 'sm' or 'lg' size to be specified.
   */
  componentSize?: TextInputSize;
  /**
   * Displays an prefix or suffix on the input
   */
  unitLabel?: string;
  /**
   * Determines the positioning of the unit label if provided
   */
  unitLabelType?: 'prefix' | 'suffix';
}

export const TextInput = React.forwardRef<HTMLInputElement, Props>(
  ({ type = 'text', unitLabel, unitLabelType = 'suffix', ...props }, ref) => {
    const typeIsUnitLabelCompatible = unitInputCompatibleTypes.includes(type);

    devWarning(
      textInputTypes.indexOf(type) >= 0,
      `TextInput type "${type}" is unsupported and may not render as expected across browsers. Supported types are: ${textInputTypes.join(
        ', ',
      )}. You should use a supported type or a more purpose-built component instead.`,
    );

    devWarning(
      limitedSupportTypes.indexOf(type) === -1,
      `TextInput type "${type}" is not natively supported in all browsers and may render as a plain text input. Consider using a more purpose-built component instead.`,
    );

    devWarning(
      !(unitLabel !== undefined && !typeIsUnitLabelCompatible),
      `TextInput type "${type}" does not support usage of unit labels.`,
    );

    return unitLabel && typeIsUnitLabelCompatible ? (
      <UnitInput
        ref={ref}
        unitLabel={unitLabel}
        unitLabelType={unitLabelType}
        {...props}
        type={type}
      />
    ) : (
      <StyledTextInput {...props} ref={ref} type={type} />
    );
  },
);

TextInput.displayName = 'TextInput';

const UnitInputTextInput = styled(TextInput)({
  border: 0,
  borderRadius: '4px 0 0 4px',
  minWidth: 0,
  flexGrow: 1,
  ':focus': {
    boxShadow: 'none',
  },
});
